import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Single source of truth for which portal the user is in. Previously this fact
 * was derived two different ways — an `adminPortal` prop drilled into Sidebar /
 * Topbar, and ad-hoc `pathname.startsWith("/admin")` checks in GlobalSearch /
 * QuickActions — which could desync on nested/aliased admin routes. PortalProvider
 * resolves it from the route ONCE and every consumer reads `usePortal()`.
 * [audit P0-3b]
 */
export type Portal = "central" | "admin";

const PortalContext = createContext<Portal | null>(null);

/** True for any route under the admin portal (handles trailing segments). */
export function portalFromPath(pathname: string): Portal {
  return pathname === "/admin" || pathname.startsWith("/admin/") ? "admin" : "central";
}

export function PortalProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const portal = useMemo(() => portalFromPath(pathname), [pathname]);
  return <PortalContext.Provider value={portal}>{children}</PortalContext.Provider>;
}

export function usePortal(): Portal {
  const ctx = useContext(PortalContext);
  // Fall back to a route read if a consumer renders outside the provider, so the
  // hook is always safe (e.g. an isolated preview cell).
  const { pathname } = useLocation();
  return ctx ?? portalFromPath(pathname);
}

export function useIsAdminPortal(): boolean {
  return usePortal() === "admin";
}
